import type { ActivityRow } from "@gioia365/contracts";
import type { Locale, MessageKey } from "@gioia365/i18n";
import type { IconName } from "../design/components/Icon";
import type { Translate } from "../i18n/locale";
import type { TimelineAnchorKind } from "../leads/timeline/focus";
import { timelineChangeLine, timelineEventTitle, timelineFieldLabel, type Change, type EventItem } from "../leads/timeline/TimelineItem";
import { outcomeText } from "../telephony/call-display";

// Che cosa dice una riga di «Attività» (SEZ-8, M7 parte 2), in parole comuni e con le parole della Timeline dove la Timeline ne ha già
// (`timelineEventTitle`, `timelineChangeLine`): la telefonata con il suo esito, la nota (mai il testo), la modifica di un campo (il
// prima e il dopo soltanto per status, owner e tag, che il server manda), gli eventi del registro. Gli eventi che la Timeline non
// mostra come eventi (i WhatsApp, che lì sono messaggi) e quelli senza Lead (Impostazioni, Smart List, Power Dialer, importazioni,
// accessi di Gioia365) hanno le loro parole qui sotto. Un'azione su un Lead che chi guarda non vede dice soltanto il suo gruppo.

type EventRow = Extract<ActivityRow, { kind: "event" }>;

/** I tipi che sono modifiche di campi nella Timeline (un «salvataggio»): il link porta al salvataggio, la riga dice il campo. */
const CHANGE_TYPES: ReadonlySet<string> = new Set([
  "lead.field_changed",
  "lead.status_changed",
  "lead.owner_changed",
  "lead.tag_added",
  "lead.tag_removed",
  "lead.phone_changed",
  "lead.second_holder_removed",
]);

/** Le modifiche di cui la riga dice anche il prima e il dopo (il server li manda soltanto per queste). */
const CHANGE_TYPES_WITH_VALUES: ReadonlySet<string> = new Set(["lead.status_changed", "lead.owner_changed", "lead.tag_added", "lead.tag_removed"]);

/** Le parole degli eventi che la Timeline non scrive come eventi, con l'icona. */
const OWN_TITLES: Readonly<Record<string, { key: MessageKey; icon: IconName }>> = {
  "whatsapp.prepared": { key: "activity.types.whatsappPrepared", icon: "whatsapp" },
  "whatsapp.declared_sent": { key: "activity.types.whatsappDeclaredSent", icon: "whatsapp" },
  "whatsapp.sent": { key: "activity.types.whatsappSent", icon: "whatsapp" },
  "whatsapp.failed": { key: "activity.types.whatsappFailed", icon: "whatsapp" },
  "whatsapp.received": { key: "activity.types.whatsappReceived", icon: "whatsapp" },
  "dialer.session_started": { key: "activity.types.dialerStarted", icon: "phone" },
  "dialer.session_interrupted": { key: "activity.types.dialerInterrupted", icon: "phone" },
  "dialer.session_finished": { key: "activity.types.dialerFinished", icon: "phone" },
  "dialer.session_paused": { key: "activity.types.dialerPaused", icon: "pause" },
  "dialer.session_resumed": { key: "activity.types.dialerResumed", icon: "play" },
  "caller_number.added": { key: "activity.types.callerNumberAdded", icon: "phone" },
  "caller_number.verified": { key: "activity.types.callerNumberVerified", icon: "phone" },
  "caller_number.verification_failed": { key: "activity.types.callerNumberVerificationFailed", icon: "alert" },
  "caller_number.assigned": { key: "activity.types.callerNumberAssigned", icon: "phone" },
  "caller_number.unassigned": { key: "activity.types.callerNumberUnassigned", icon: "phone" },
  "caller_number.removed": { key: "activity.types.callerNumberRemoved", icon: "phone" },
  "whatsapp_sender.set": { key: "activity.types.whatsappSenderSet", icon: "whatsapp" },
  "whatsapp_template.updated": { key: "activity.types.whatsappTemplateUpdated", icon: "whatsapp" },
  "whatsapp_settings.updated": { key: "activity.types.whatsappSettingsUpdated", icon: "whatsapp" },
  "settings.lead_fields_published": { key: "activity.types.leadFieldsPublished", icon: "settings" },
  "settings.lead_visibility_changed": { key: "activity.types.leadVisibilityChanged", icon: "settings" },
  "settings.table_layout_published": { key: "activity.types.tableLayoutPublished", icon: "columns" },
  "settings.call_sequence_changed": { key: "activity.types.callSequenceChanged", icon: "settings" },
  "settings.whatsapp_channels_changed": { key: "activity.types.whatsappChannelsChanged", icon: "settings" },
  "settings.assignment_changed": { key: "activity.types.assignmentChanged", icon: "settings" },
  "settings.report_goals_changed": { key: "activity.types.reportGoalsChanged", icon: "report" },
  "settings.work_blocks_changed": { key: "activity.types.workBlocksChanged", icon: "report" },
  "settings.lead_intake_changed": { key: "activity.types.leadIntakeChanged", icon: "settings" },
  "intake.address_copied": { key: "activity.types.intakeAddressCopied", icon: "settings" },
  "intake.address_rotated": { key: "activity.types.intakeAddressRotated", icon: "settings" },
  "smart_list.created": { key: "activity.types.smartListCreated", icon: "smartlist" },
  "smart_list.saved": { key: "activity.types.smartListSaved", icon: "smartlist" },
  "smart_list.shared": { key: "activity.types.smartListShared", icon: "smartlist" },
  "smart_list.renamed": { key: "activity.types.smartListRenamed", icon: "smartlist" },
  "smart_list.archived": { key: "activity.types.smartListArchived", icon: "smartlist" },
  "tag.created": { key: "activity.types.tagCreated", icon: "tag" },
  "import.completed": { key: "activity.types.importCompleted", icon: "down" },
  "lead.deleted": { key: "activity.types.leadDeleted", icon: "trash" },
  // La Timeline nomina il telefono, che qui non arriva.
  "lead.phone_marked_wrong": { key: "activity.types.phoneMarkedWrong", icon: "phone" },
  "platform.organization_access": { key: "activity.types.platformAccess", icon: "building" },
};

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value : null);

export type RowView = { icon: IconName; title: string; detail: string | null };

/** La telefonata in una riga, con le parole della Timeline: «Chiamata in uscita · Risposta · Richiamare». */
function callView(t: Translate, row: Extract<ActivityRow, { kind: "call" }>): RowView {
  const direction = row.direction === "inbound" ? t("timeline.call.inbound") : t("timeline.call.outbound");
  let outcome: string;
  if (row.leadHidden) return { icon: "phone", title: direction, detail: null };
  if (row.live) outcome = t("timeline.call.live");
  else if (!row.valid) outcome = t("timeline.call.failed");
  else if (row.outcome === null) outcome = t("timeline.call.unknownOutcome");
  else outcome = outcomeText(t, row.outcome, row.answeredOutcome, null);
  return { icon: "phone", title: `${direction} · ${outcome}`, detail: row.source === "manual" ? t("timeline.call.manual") : null };
}

function eventView(t: Translate, locale: Locale, timeZone: string, row: EventRow): RowView {
  const details = row.details;
  if (details === null) return { icon: "lead", title: t("activity.hiddenAction", { group: t(`activity.groups.${row.group}`) }), detail: null };
  const own = OWN_TITLES[row.type];
  if (own) {
    const payload = details.payload;
    const title = t(own.key, { name: text(payload.name) ?? text(payload.sourceName) ?? "—" });
    let detail: string | null = null;
    if (row.type === "platform.organization_access") detail = t("activity.platformPath", { method: text(payload.method) ?? "—", path: text(payload.path) ?? "—" });
    // RUOLI-15: i ruoli di livello 1 senza Lead che «Chi vede i Lead» ha lasciato com'erano.
    const skipped = Array.isArray(payload.skippedRoles) ? payload.skippedRoles.filter((name): name is string => typeof name === "string") : [];
    if (skipped.length > 0) detail = t("activity.skippedRoles", { names: skipped.join(", ") });
    return { icon: own.icon, title, detail };
  }
  if (CHANGE_TYPES.has(row.type)) {
    const change: Change = { type: row.type, fieldRef: null, fieldLabel: details.fieldLabel, before: details.before, after: details.after, reason: details.reason };
    const title = CHANGE_TYPES_WITH_VALUES.has(row.type) ? timelineChangeLine(t, locale, change) : t("timeline.change.one", { field: timelineFieldLabel(t, change) });
    return { icon: row.type === "lead.owner_changed" ? "user" : row.type.startsWith("lead.tag") ? "tag" : "note", title, detail: null };
  }
  const item: EventItem = {
    kind: "event",
    id: row.eventId,
    occurredAt: row.occurredAt,
    occurredAtKnown: true,
    actor: row.actor,
    fromClose: row.fromClose,
    type: row.type,
    payload: details.payload,
    reason: details.reason,
    before: (details.before ?? null) as Record<string, unknown> | null,
    after: (details.after ?? null) as Record<string, unknown> | null,
  };
  const { icon, title } = timelineEventTitle(t, locale, timeZone, item);
  return { icon, title, detail: null };
}

/** Che cosa dice la riga: l'icona, il titolo e, se serve, una seconda riga. */
export function rowViewOf(t: Translate, locale: Locale, timeZone: string, row: ActivityRow): RowView {
  switch (row.kind) {
    case "call":
      return callView(t, row);
    case "note":
      return { icon: "note", title: row.callId === null ? t("activity.note") : t("activity.memo"), detail: null };
    case "event":
      return eventView(t, locale, timeZone, row);
  }
}

/**
 * Il Lead la cui scheda ha la Timeline dell'azione: il Lead stesso; per un Lead unito quello in cui è entrato (l'unione vi sposta
 * telefonate, note ed eventi), se chi guarda lo vede; per un Lead archiviato nessuno, perché non ha più una scheda.
 */
export function timelineLeadOf(row: ActivityRow): string | null {
  if (row.lead === null) return null;
  if (row.lead.state === "archived") return null;
  if (row.lead.state === "merged") return row.lead.mergedInto?.id ?? null;
  return row.lead.id;
}

/**
 * L'elemento della Timeline a cui porta la riga (`?evento=<tipo>:<id>` della scheda di `timelineLeadOf`), oppure `null`: senza una
 * Timeline da aprire, e per un WhatsApp di cui l'evento non dice il messaggio. Una modifica porta al suo salvataggio, l'appunto alla
 * sua telefonata.
 */
export function timelineTargetOf(row: ActivityRow): { kind: TimelineAnchorKind; id: string } | null {
  if (timelineLeadOf(row) === null) return null;
  switch (row.kind) {
    case "call":
      return { kind: "call", id: row.callId };
    case "note":
      return row.callId === null ? { kind: "note", id: row.noteId } : { kind: "call", id: row.callId };
    case "event": {
      if (CHANGE_TYPES.has(row.type)) return { kind: "change", id: row.correlationId };
      if (row.type.startsWith("whatsapp.")) {
        const messageId = text(row.details?.payload.messageId);
        return messageId === null ? null : { kind: "whatsapp", id: messageId };
      }
      return { kind: "event", id: row.eventId };
    }
  }
}
